import { useMemo } from "react";
import dayjs from "dayjs";
import { useProfile, useEvents, useReservations, useNeighborhoods } from "@/lib/api";
import type { UserAPI } from "@shared/types/api";
import { BOOKING_CUTOFF_HOURS } from "@shared/constants";
import { eventTimeToDaysjs } from "@/utils/dateUtils";

export interface BookableEvent {
  id: string;
  date: string;
  time: string;
  type: string | null;
  neighborhoodIds: string[] | null;
}

/**
 * Upcoming events the signed-in user could actually book, and the launched
 * neighbourhoods they belong to.
 *
 * Three separate things disqualify an event, and all three have to agree:
 *
 *   the booking window   closes `BOOKING_CUTOFF_HOURS` before the sitting
 *   an existing seat     they already hold an active reservation for it
 *   the neighbourhood    has launched in their city, before the event's date
 *
 * This is the logic `BookingContext` runs, lifted out of it. The context
 * itself can't be reused outside `/booking`: it owns that page's URL, so
 * selecting an event there navigates, and mounting it redirects anyone who
 * already holds a booking. Onboarding needs the rules without the page.
 */
export function useBookableEvents(cityIdOverride?: string): {
  events: BookableEvent[];
  /** Launched neighbourhoods for the user's city, in the order served. */
  neighborhoods: Array<{ id: string; name: string }>;
  isLoading: boolean;
} {
  const { data: profileData, isLoading: profileLoading } = useProfile();
  // `/user/events` serves every available sitting; the city filter is entirely
  // client-side, through that city's launched neighbourhoods. So looking at
  // another city is a matter of asking for its neighbourhoods instead.
  const cityId = cityIdOverride || profileData?.data?.user?.city;
  const { data: eventsData, isLoading: eventsLoading } = useEvents();
  const { data: reservationsData } = useReservations();
  const { data: neighborhoodsData, isLoading: neighborhoodsLoading } =
    useNeighborhoods(undefined, cityId || undefined);

  const neighborhoods = useMemo(
    () => neighborhoodsData?.data || [],
    [neighborhoodsData?.data],
  );

  const events = useMemo(() => {
    const activeReservationIds = new Set(
      (reservationsData?.data || [])
        .filter((r: UserAPI.Reservation) => r.status === "active")
        .map((r: UserAPI.Reservation) => r.eventTimeId),
    );
    return (eventsData?.data || []).filter((event) => {
      const beforeCutoff = eventTimeToDaysjs(event)
        .subtract(BOOKING_CUTOFF_HOURS, "hours")
        .isAfter(dayjs());
      if (!beforeCutoff || activeReservationIds.has(event.id)) return false;
      if (neighborhoods.length === 0) return false;
      return neighborhoods.some((n) => {
        if (event.neighborhoodIds && !event.neighborhoodIds.includes(n.id))
          return false;
        // `launchedAfter` is served but missing from the shared response type,
        // so it is narrowed here rather than casting the whole neighbourhood
        // to `any` the way the booking pages do.
        const { launchedAfter } = n as { launchedAfter?: string | null };
        if (!launchedAfter) return true;
        return event.date > launchedAfter;
      });
    });
  }, [eventsData?.data, reservationsData?.data, neighborhoods]);

  return {
    events,
    neighborhoods,
    isLoading: profileLoading || eventsLoading || neighborhoodsLoading,
  };
}
